import { useState } from "react";
import { useSession } from "../data/usePage.js";
import { invalidate } from "../data/useFetch.js";
import { addHat, removeHat, renameHat, setHat } from "../api.js";
import { SystemPage } from "./SystemPage.js";

/**
 * `/_hats` — manage your own hats (docs/auth/Hats.md): add, remove, rename,
 * and wear. A hat is a suffix on your identity (`you/hat`); what each grants is
 * decided by config (groups/access), so a hat you hold isn't powerful until an
 * admin lists it. Switching also lives in the avatar menu.
 */
export function HatsView() {
  const session = useSession();
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (session.status !== "ready") return <SystemPage title="Hats">Loading…</SystemPage>;
  if (!session.data.user)
    return (
      <SystemPage title="Hats">
        <p>
          <a href="/auth/login">Sign in</a> to manage hats.
        </p>
      </SystemPage>
    );

  const { hats, hat } = session.data;

  // Run a hat mutation, then refetch session (+ content, since the worn identity
  // may change). Surface errors.
  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      invalidate("session");
      invalidate("tree");
      invalidate("page:");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SystemPage
      title="Hats"
      lead="Each hat is a separate identity (you/hat); what it can do is set by config groups/access. Wearing one switches who you are right now."
    >
      <ul className="dw-hats-list">
        {hats.length === 0 && <li className="dw-muted">No hats yet — add one below.</li>}
        {hats.map((h) => (
          <li key={h} className="dw-hats-item">
            <code className="dw-access-subject">/{h}</code>
            {hat === h ? (
              <span className="dw-muted">worn</span>
            ) : (
              <button className="dw-link" type="button" disabled={busy} onClick={() => void run(() => setHat(h))}>
                wear
              </button>
            )}
            <button
              className="dw-link"
              type="button"
              disabled={busy}
              onClick={() => {
                const to = window.prompt(`Rename /${h} to:`, h);
                if (to && to !== h) void run(() => renameHat(h, to));
              }}
            >
              rename
            </button>
            <button
              className="dw-link dw-danger-link"
              type="button"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Remove the /${h} hat?`)) void run(() => removeHat(h));
              }}
            >
              remove
            </button>
          </li>
        ))}
        {hat && (
          <li className="dw-hats-item">
            <button className="dw-link" type="button" disabled={busy} onClick={() => void run(() => setHat(null))}>
              take off (no hat)
            </button>
          </li>
        )}
      </ul>

      <form
        className="dw-hats-add"
        onSubmit={(e) => {
          e.preventDefault();
          const name = adding.trim();
          if (name) void run(() => addHat(name)).then(() => setAdding(""));
        }}
      >
        <input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          placeholder="new hat name (e.g. admin)"
          disabled={busy}
        />
        <button className="dw-btn" type="submit" disabled={busy || !adding.trim()}>
          add hat
        </button>
      </form>

      {err && <p className="dw-error">{err}</p>}
      <p className="dw-muted">
        Holding a hat doesn't grant anything until config lists <code>you/{hat || "hat"}</code> in a
        group — manage that under <a href="/_access">access</a> (admins).
      </p>
    </SystemPage>
  );
}
